import PropTypes from 'prop-types';
import React from 'react';
import { useTranslations } from 'next-intl';

import track from '@helper/tracking';
import {
  FacebookBorderIcon,
  InstagramIcon,
  LinkedInIcon,
  ThreadsIcon,
  YoutubeIcon
} from '../../icons/sharing/sharing';
import Container from '../../layout/container/container';
import CustomLink from '../../atoms/custom-link/customLink';
import LogoIcon from '../../icons/logo/logo';
import StyledFooter from './footer.style';

const getYear = () => {
  const date = new Date();
  const year = date.getFullYear();

  return year;
};

const renderIcon = () => ({
  instagram: <InstagramIcon />,
  facebook: <FacebookBorderIcon />,
  youtube: <YoutubeIcon />,
  linkedIn: <LinkedInIcon />,
  threads: <ThreadsIcon />
});

const Footer = ({
  footer: { socialMedia = [], contentPages = [], metaPages = [] },
  autoLogout = false,
  noDots = false,
  isArchivePage = false
}) => {
  const t = useTranslations();
  const handleTracking = pageTitle => {
    track(['trackEvent', 'Footer', 'Click', pageTitle]);
  };

  const COOKIE_TEXT = 'Cookies & Tracking';
  const handleCookiePanel = () => {
    track(['trackEvent', 'Footer', 'Click', COOKIE_TEXT]);
    window.CookieFirst.openPanel();
  };

  return (
    <StyledFooter noDots className={`${isArchivePage ? 'isArchivePage' : ''}`}>
      {!noDots && !isArchivePage && <div className="footer__dots" />}

      {!isArchivePage && (
        <div className="footer__wrapper">
          <Container large>
            <LogoIcon />
            <div className="footer__content">
              <nav className="footer__navigation" aria-label={t('nav.pages')}>
                <ul>
                  {contentPages.map((page, index) => (
                    <li key={`${page.slug}_${index}`}>
                      <CustomLink
                        link={page}
                        onClick={() => handleTracking(page.title)}
                        onKeyDown={e => (e.key === 'Enter' ? handleTracking(page.title) : '')}
                      >
                        {page.title}
                      </CustomLink>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </Container>
        </div>
      )}

      <div className="footer__sub">
        <Container large>
          {!autoLogout && !isArchivePage && (
            <nav className="footer__social" aria-label={t('nav.socialMedia')}>
              {socialMedia.map(social => (
                <a href={social.url} target="_blank" rel="noopener noreferrer" key={social.type}>
                  {social.type && <span className="sr-only">{social.type}</span>}
                  {renderIcon()[social.type]}
                </a>
              ))}
            </nav>
          )}
          <nav className="footer__sub-navigation" aria-label={t('nav.rights')}>
            <ul>
              {metaPages.map(page => (
                <li key={page.slug}>
                  <CustomLink
                    key={page.slug}
                    link={page}
                    onClick={() => handleTracking(page.title)}
                    onKeyDown={e => (e.key === 'Enter' ? handleTracking(page.title) : '')}
                  >
                    {page.title}
                  </CustomLink>
                </li>
              ))}
              <li>
                <a
                  role="button"
                  tabIndex="0"
                  onClick={handleCookiePanel}
                  onKeyDown={e => e.key === 'Enter' && handleCookiePanel()}
                >
                  {COOKIE_TEXT}
                </a>
              </li>
            </ul>
          </nav>
          <p className="footer__copyright">© Copyright {getYear()} Futurium</p>
        </Container>
      </div>
    </StyledFooter>
  );
};

Footer.propTypes = {
  autoLogout: PropTypes.bool,
  footer: PropTypes.shape({
    socialMedia: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        uri: PropTypes.string
      })
    ),
    contentPages: PropTypes.arrayOf(
      PropTypes.shape({
        uri: PropTypes.string,
        title: PropTypes.string
      })
    ),
    metaPages: PropTypes.arrayOf(
      PropTypes.shape({
        uri: PropTypes.string,
        title: PropTypes.string
      })
    )
  }),
  noDots: PropTypes.bool,
  isArchivePage: PropTypes.bool
};

export default Footer;
